/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
import { Box, TableContainer } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';

import { StatusCode } from '../../api/enumerations';
import { getAllErrorReports } from '../../api/errorReport';
import { ErrorReportData } from '../../api/errorReport/types';
import Snackbar from '../../components/Snackbar';
import { TitleBox } from '../../components/UI/Box';
import { GridMain } from '../../components/UI/Grid';
import {
  StyledLinearProgress,
  TitleTypography,
} from '../../components/UI/Typography';
import { Constants } from '../../constants/errorReport';
import { IconWarningMS } from '../../constants/icons';
import { GlobalContext } from '../../context/global';
import useErrorMessage from '../../hooks/useErrorMessage';
import { ReportTable } from './ReportTable';
import { Search } from './Search';
import { StyledToggleButton, StyledToggleButtonGroup } from './styles';

export function ErrorReport(): JSX.Element {
  const {
    openSnackbar,
    setOpenSnackbar,
    setErrorMessage,
    setSnackbarMessage,
    errorFilters,
    setErrorFilters,
  } = useContext(GlobalContext);

  const [reports, setReports] = useState<ErrorReportData[]>();
  const [loading, setLoading] = useState(false);
  const [searchField, setSearchField] = useState(errorFilters.searchTerm);
  const [totalRecords, setTotalRecords] = useState(0);
  const rowsPerPage = 8;
  const { page, status, type, searchTerm } = errorFilters;

  const { getErrorMessage } = useErrorMessage();

  const getDataCallback = useCallback(async () => {
    setLoading(true);
    const currentPage = page + 1;

    try {
      const response = await getAllErrorReports(
        currentPage,
        rowsPerPage,
        status ? status === 'finalizados' : undefined,
        type || undefined,
        searchTerm || undefined
      );

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente');

      if (response.detail.total_records)
        setTotalRecords(response.detail.total_records);

      setReports(response.data);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    status,
    type,
    searchTerm,
    setErrorMessage,
    setOpenSnackbar,
    setSnackbarMessage,
  ]);

  useEffect(() => {
    getDataCallback();
  }, [getDataCallback]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setErrorFilters((prev) => ({
        ...prev,
        searchTerm: searchField,
        page: 0,
      }));
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchField]);

  const handleStatus = (
    event: React.MouseEvent<HTMLElement>,
    newStatus: string
  ): void => {
    setErrorFilters((prev) => ({ ...prev, status: newStatus, page: 0 }));
  };

  const handleType = (
    event: React.MouseEvent<HTMLElement>,
    newType: number
  ): void => {
    setErrorFilters((prev) => ({ ...prev, type: newType, page: 0 }));
  };

  return (
    <GridMain>
      <TitleBox>
        <TitleTypography>
          {IconWarningMS}
          {Constants.reportedErrors}
        </TitleTypography>
      </TitleBox>
      <TableContainer>
        <StyledToggleButtonGroup
          value={status}
          exclusive
          onChange={handleStatus}
          aria-label="status"
        >
          <StyledToggleButton value="abertos" aria-label="abertos">
            abertos
          </StyledToggleButton>
          <StyledToggleButton value="finalizados" aria-label="finalizados">
            finalizados
          </StyledToggleButton>
        </StyledToggleButtonGroup>
        <StyledToggleButtonGroup
          value={type}
          exclusive
          onChange={handleType}
          aria-label="tipo"
        >
          <StyledToggleButton value={1} aria-label="elemento">
            elemento
          </StyledToggleButton>
          <StyledToggleButton value={2} aria-label="relatorio">
            relatório
          </StyledToggleButton>
        </StyledToggleButtonGroup>
        <Box sx={{ float: 'right' }}>
          <Search searchField={searchField} setSearchField={setSearchField} />
        </Box>
        {loading ? (
          <StyledLinearProgress />
        ) : (
          <Box>
            {reports && reports.length > 0 ? (
              <ReportTable
                tableData={reports}
                page={page}
                setPage={(newPage) =>
                  setErrorFilters((prev) => ({ ...prev, page: newPage }))
                }
                totalRecords={totalRecords}
                rowsPerPage={rowsPerPage}
              />
            ) : (
              Constants.noreports
            )}
          </Box>
        )}
      </TableContainer>
      {openSnackbar && <Snackbar />}
    </GridMain>
  );
}
